import classes from "./ProductItem.module.less";
import { saveSearchTerm } from "~/Header/components/headerMenu/Autosuggest/api/saveSearchTerm";
import type { SrpProductData } from "~/utils/models";
import { forwardRef } from "react";
import { ProductContext } from "~/feature/Product/ProductContext";
import { Product } from "~/feature/Product";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";
import { Link, LinkType } from "~/common/components/Link";
import { TextWithHighlight } from "~/common/components/TextWithHighlight";
import { SideButton } from "~/common/components/Button/SideButton";
import { IconType } from "~/common/components/icon/IconType";
import { Button } from "~/common/components/Button/Button";
import { Icon } from "~/common/components/icon/Icon";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { canSeeAddToCartButton } from "~/utils/features";
import { useAddProductToWishlistFlow } from "~/common/hooks/useAddProductToWishlistFlow";
import { closeModalById } from "~/AddToCart/ModalSevice";
import { AddToCartService } from "~/AddToCart/AddToCartService";
import { useTranslation } from "react-i18next";

interface Props {
  className?: string;
  product: SrpProductData;
  query?: string;
  hide: () => void;
}

export const ProductItem = forwardRef<HTMLAnchorElement, Props>(
  ({ product, className, query = "", hide }: Props, ref) => {
    const { t } = useTranslation();

    const hasSales = Object.keys(product.salesCampaign).length > 0;

    const linkProps = {
      onClick: () => saveSearchTerm(product.designation, `${product.url}?text=${product.designation}`),
      href: `${product.url}?text=${product.designation}`,
      ["tracking-aux-sch-prod-name"]: product.designation,
      ["tracking-aux-sch-prod-id"]: product.sapMaterialNumber,
      ["data-prod-article"]: product.hybrisCode,
    };

    const [addToWishlist, addToWishlistModal] = useAddProductToWishlistFlow({});

    const addToCart = (e: React.MouseEvent, productCode: string, restrictionQuery: string) => {
      e.preventDefault();
      e.stopPropagation();
      closeModalById("add-to-cart-dialog");
      return AddToCartService.openAddToCartWizard({
        productCode: productCode,
        restrictionQuery: restrictionQuery,
        source: "autocomplete",
      });
    };

    const screenSize = useNumericScreenSize();
    const isMobile = screenSize <= useNumericScreenSize.phone;
    const canSeeAddToCart = canSeeAddToCartButton();

    let buttons;
    if (canSeeAddToCart) {
      const commonProps = {
        ["tracking-aux-sch-addtc-prod-name"]: product.designation,
        ["tracking-aux-sch-addtc-prod-id"]: product.sapMaterialNumber,
        onClick: (e: React.MouseEvent) => {
          addToCart(e, product.code, query).then(() => hide());
        },
      };
      if (isMobile) {
        buttons = <SideButton color="primary" icon={IconType.Cart} {...commonProps} />;
      } else {
        buttons = (
          <Button size="medium" startIcon={<Icon type={IconType.Cart} />} {...commonProps}>
            {t("recentlyViewProducts.addProducts")}
          </Button>
        );
      }
    } else {
      const addToWishlistBtn = (
        <SideButton onClick={() => addToWishlist(product)} color="primary" icon={IconType.Bookmark} />
      );
      if (isMobile) {
        buttons = addToWishlistBtn;
      } else {
        buttons = (
          <>
            <SideButton
              href={product.cadUrl}
              className={classes.sideButton}
              color="primary"
              icon={IconType.Cad}
              disabled={!product.cadUrl}
            />
            {addToWishlistBtn}
            <SideButton
              href={product.calcUrl}
              className={classes.sideButton}
              color="primary"
              icon={IconType.Calculator}
              disabled={!product.calcUrl}
            />
          </>
        );
      }
    }

    return (
      <ProductContext value={product}>
        <div
          className={classes.root}
          asgsta-product-parent-marker="true"
          asgsta-is-campaign-belonging={`${hasSales}`}
          asgsta-campaign={Object.values(product.salesCampaign)[0]?.[0].code ?? "None"}
          data-zoovu="searchSuggestion"
        >
          <Product.PrimaryImage className={classes.image} linkProps={{ ...linkProps, tabIndex: -1 }} />
          <div className={classes.designationContainer}>
            <Product.PreferredIndicator noMargin />
            <Link {...linkProps} ref={ref} variant={LinkType.NAVIGATION}>
              <TextWithHighlight highlight={query} highlightClass={"f-normal"}>
                {product.designation}
              </TextWithHighlight>
            </Link>

            {hasSales && <SaleTag size={16} />}
          </div>
          <Product.ShortDescription className={classes.description} linkProps={{ ...linkProps, tabIndex: -1 }} />

          <div className={classes.buttons}>{buttons}</div>
        </div>
        {addToWishlistModal}
      </ProductContext>
    );
  },
);
